/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useMemo, useRef, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

// Components
import {
    Box,
    IconButton,
    Link as ChakraLink,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HeartIcon, HeartSolidIcon} from '../icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '../with-registration'
import ProductTileDetails from './partials/product-tile-details'
import ProductTileMedia from './partials/product-tile-media'
import ProductTileMobileQuickView from './partials/product-tile-mobile-quickview'
import ProductSetQuickView from '../product-set-quick-view'
import {ProductTileSkeleton} from './partials/product-tile-skeleton'

// Hooks
import {useProduct, usePromotions} from '@salesforce/commerce-sdk-react'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {useVariationByAttributeValues} from '../../hooks/use-variant-by-attribute-values'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {useBrand} from '../../contexts/brand-context'
import useProductTileCart from './hooks/use-product-tile-cart'
import useProductTileHover from './hooks/use-product-tile-hover'
import useWishlistCount from './hooks/use-wishlist-count'
import useProductTileMode from './hooks/use-product-tile-mode'
import useHasOpened from './hooks/use-has-opened'
import useQuickAddPrefetch from './hooks/use-quick-add-prefetch'
import {useInViewport} from '../../hooks/use-in-viewport'
import {useProductAvailability} from '../../hooks/use-product-availability'

// Other
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {productUrlBuilder, rebuildPathWithParams} from '../../utils/url'
import {getPriceData, isGiftCardProduct} from '../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {PRODUCT_BADGE_DETAILS} from '@salesforce/retail-react-app/app/constants'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useAddToCartConfirmation} from '../../contexts/add-to-cart-confirmation-context'
import {
    buildImageUrl,
    deriveNthImageUrl,
    getProductPromotionIds,
    getProductTileImages,
    getTileBadgeRules,
    getVisibleTileBadges,
    hasTileColorSwatches,
    buildTileMediaItems
} from './utils/product-tile-utils'
import productTilePropTypes from './utils/product-tile-proptypes'
import {
    trackAddToWishlistEvent,
    trackRemoveFromWishlistEvent
} from '../../utils/optimizely-web-exp-utils'
import {
    buildProductTileSelectItemProduct,
    resolveProductTileViewItemLocation,
    sendViewItemEvent
} from '../../utils/gtm-utils'

const findVariantByProductId = (product, productId) =>
    product?.variants?.find((variant) => variant?.productId === productId)

const IconButtonWithRegistration = withRegistration(IconButton, {isWishlistAction: true})

export const Skeleton = ProductTileSkeleton

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = forwardRef((props, ref) => {
    const location = useLocation()

    const {
        dynamicImageProps,
        videoProps,
        enableFavourite = true,
        enableWishlistCount,
        disableFavouriteInteraction = false,
        imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE,
        mainImageNumber,
        isFavourite,
        onFavouriteToggle,
        product = {},
        selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID,
        badgeDetails = PRODUCT_BADGE_DETAILS,
        isRefreshingData = false,
        showSwatches,
        showVideo = false,
        showDetails = true,
        showProductLikes = true,
        showQV = true,
        isShopTheLook = false,
        isOOS = false,
        setQuickViewProduct,
        setQuickViewWishlistInfo,
        setMobileQuickViewOpen,
        isWishlistPage = false,
        disableImageScroll = false,
        showScrollHint = false,
        overwriteProductName = null,
        responsiveMediaBlock = null,
        variant: stylingVariant,
        onClick,
        onQuickViewOpen,
        onQuickViewClose,
        isLayout = false,
        // Extract CMS styling props to prevent them from being passed to DOM
        productTitleStyle,
        productPriceStyle,
        // GDI carousel-driven on/off toggles. Each entry is a Chakra responsive
        // `display` value applied to its corresponding subcomponent. Default
        // undefined → no override → existing behavior is preserved (PLP /
        // recommendations / wishlist all leave this prop unset).
        responsiveDisplay,
        // GDI carousel-forced tile mode ('quick-view' | 'quick-add'), passed
        // as a Chakra responsive object. When set, it overrides the
        // screensize-based `useProductTileMode` decision for the Quick Add
        // overlay.
        forceTileMode,
        ...rest
    } = props
    const {imageGroups, productId: initialProductId, id, representedProduct, variants} = product
    const tileRef = useRef(null)
    // One viewport ahead: gates the swatch-row stock check and the quick-add pre-warm.
    const {observeRef: observeTileRef, hasBeenInViewport: isNearViewport} = useInViewport({
        rootMargin: '100% 0px'
    })
    const setTileRef = useCallback(
        (node) => {
            tileRef.current = node
            observeTileRef(node)
            if (typeof ref === 'function') {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        },
        [ref, observeTileRef]
    )

    // For variation groups, productId may be undefined, so we use id as fallback
    // This ensures QuickView can fetch product data correctly
    const effectiveInitialProductId = initialProductId || id
    const [productId, setProductId] = useState(effectiveInitialProductId)

    // Prefer product.id, but fall back to product.productId for PLP search hits.
    // This guarantees we can persist/replay wishlist intent after login.
    const wishlistProductId = id || initialProductId

    // Ensure productId is always valid - if it becomes undefined, reset it
    useEffect(() => {
        if (!productId && effectiveInitialProductId) {
            setProductId(effectiveInitialProductId)
        }
    }, [productId, effectiveInitialProductId])
    const {onOpen: onAddToCartModalOpen, onAutoClose: onAddToCartModalClose} = useMiniCartContext()
    const {setConfirmationTrigger} = useAddToCartConfirmation()

    const intl = useIntl()
    const {currency} = useCurrency()
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const [isHovered, setIsHovered] = useState(false)
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false)

    // Mobile Quickview State
    const [mobileQuickViewOpen, setMobileQuickViewOpenInternal] = useState(false)
    // Mobile Quickview State for product sets
    const [setQuickViewOpen, setSetQuickViewOpen] = useState(false)
    // Both drawers mount on first open; a closed Drawer per tile was pure hydration overhead.
    const hasOpenedMobileQuickView = useHasOpened(mobileQuickViewOpen)
    const hasOpenedSetQuickView = useHasOpened(setQuickViewOpen)

    // Track variant selected in QuickView to update tile price
    const [selectedVariantId, setSelectedVariantId] = useState(null)

    // Track product selected in QuickView
    const [qvProduct, setQvProduct] = useState(null)

    // Track size/length selections made in QuickView so productUrl carries them to PDP
    const [qvLocalSelections, setQvLocalSelections] = useState({})

    const selectedTileProductId = productId || effectiveInitialProductId
    // Fetch the selected color's full (price-bearing) product when a swatch changes color.
    const shouldFetchSelectedTileProduct =
        !!selectedTileProductId && selectedTileProductId !== effectiveInitialProductId
    const {
        data: selectedTileProduct,
        isLoading: isSelectedTileProductLoading,
        isFetching: isSelectedTileProductFetching
    } = useProduct(
        {
            parameters: {
                id: selectedTileProductId,
                perPricebook: true,
                expand: ['images', 'prices', 'promotions', 'variations'],
                allImages: true
            }
        },
        {
            enabled: shouldFetchSelectedTileProduct,
            keepPreviousData: true
        }
    )

    // Fresh orderable/inventory for the selected color via the Shopper Availability API.
    const {data: selectedTileAvailabilityMap} = useProductAvailability(selectedTileProductId, {
        expandVariations: true,
        enabled: shouldFetchSelectedTileProduct
    })
    const selectedTileProductWithAvailability = useMemo(() => {
        if (!selectedTileProduct) return selectedTileProduct
        const availability = selectedTileAvailabilityMap?.[selectedTileProduct.id]
        if (!availability) return selectedTileProduct
        const variantOrderableMap = new Map(
            availability.variants?.map((v) => [v.productId, v.orderable]) || []
        )
        const variants = selectedTileProduct.variants?.map((variant) =>
            variantOrderableMap.has(variant.productId)
                ? {...variant, orderable: variantOrderableMap.get(variant.productId)}
                : variant
        )
        return {
            ...selectedTileProduct,
            inventory: availability.inventory || selectedTileProduct.inventory,
            variants: variants || selectedTileProduct.variants
        }
    }, [selectedTileProduct, selectedTileAvailabilityMap])
    const selectedTileProductMatchesSelectedProduct =
        selectedTileProduct &&
        (selectedTileProduct.id === selectedTileProductId ||
            selectedTileProduct.productId === selectedTileProductId)
    const isSelectedTileProductPriceLoading =
        shouldFetchSelectedTileProduct &&
        !selectedTileProductMatchesSelectedProduct &&
        (isSelectedTileProductLoading || isSelectedTileProductFetching)

    // Wishlist hook
    const {isInWishlist, toggleWishlist, removeItemFromWishlist} = useWishlistActions()

    const hasLengthVariant = Array.isArray(product?.variants)
        ? product.variants.some(
              (variant) =>
                  variant?.variationValues &&
                  typeof variant.variationValues === 'object' &&
                  'length' in variant.variationValues &&
                  variant.variationValues.length
          )
        : false

    const isMasterVariant = !!variants
    const initialVariationValue =
        isMasterVariant && !!representedProduct
            ? variants?.find((variant) => variant.productId == product.representedProduct.id)
                  ?.variationValues?.[selectableAttributeId]
            : undefined

    // I added this because the representedProduct is available only on the useProductSearch hook so the price will not be properly rendered
    const defaultColorVariationValue =
        isMasterVariant && !initialVariationValue ? product?.variationValues?.color : undefined

    let [colorValue, setColorValue] = useState(initialVariationValue || defaultColorVariationValue)
    const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
    const [isShopSimilarModalOpen, setIsShopSimilarModalOpen] = useState(false)

    const productObject = product
    // Price comes from the swatch click: the search hit (all colors' variant prices) or,
    // once a color changes, its full fetch. Quick add never feeds price back in.
    const pricingProduct =
        (selectedTileProductMatchesSelectedProduct && selectedTileProductWithAvailability) ||
        product

    const initialColorValueRef = useRef(colorValue)
    const hasColorChangedRef = useRef(false)
    const lastViewItemColorRef = useRef(null)

    useEffect(() => {
        if (
            !colorValue ||
            (colorValue === initialColorValueRef.current && !hasColorChangedRef.current)
        ) {
            return
        }
        hasColorChangedRef.current = true

        if (shouldFetchSelectedTileProduct && !selectedTileProductMatchesSelectedProduct) {
            return
        }

        if (lastViewItemColorRef.current === colorValue) {
            return
        }
        lastViewItemColorRef.current = colorValue

        sendViewItemEvent(
            {
                ...pricingProduct,
                productId: selectedTileProductId,
                c_variationGroup: selectedTileProductId
            },
            resolveProductTileViewItemLocation(location.pathname, isWishlistPage)
        )
    }, [
        colorValue,
        pricingProduct,
        selectedTileProductId,
        shouldFetchSelectedTileProduct,
        selectedTileProductMatchesSelectedProduct,
        location.pathname,
        isWishlistPage
    ])

    const isWishlistEnabled =
        enableWishlistCount ?? Boolean(productObject?.c_enableWishlistCount)
    const {wishlistCount, setWishlistCount, isLoadingFreshLikes, triggerFreshLikesFetch} =
        useWishlistCount({
            product,
            productId: productId || id,
            locationPathname: location.pathname,
            enabled: isWishlistEnabled
        })

    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {isGarage, isDynamite} = useBrand()
    const isProductTileMediaCarouselEnabled =
        getSiteEnvVarValue(appConfig, 'PRODUCT_TILE_MEDIA_CAROUSEL_ENABLED', site?.id) === 'true'

    // Skip QuickView (show "Add to bag" button) ONLY for true variant products in Wishlist
    // - Variants: have type.variant === true (specific color/size already selected)
    // - Masters/VariationGroups: have type.variationGroup === true or type.master === true (need attribute selection)
    const isActualVariant = product?.type?.variant === true
    // product.type is present on full product fetches; product.productType is used in SCAPI search hits (PLP/search)
    const isProductSet = product?.type?.set === true || product?.productType?.set === true

    const isGiftCard = isGiftCardProduct(productObject)
    useQuickAddPrefetch({
        product,
        productId: selectedTileProductId,
        enabled: isNearViewport && !isProductSet && !isGiftCard
    })

    const ShouldSkipQuickView = (isActualVariant && isWishlistPage) || isGiftCard

    // Determine wishlist status
    const isProductInWishlist =
        isFavourite !== undefined ? isFavourite : isInWishlist(productId || id)

    // Handle favourite toggle
    const handleFavouriteClick = async () => {
        if (onFavouriteToggle) {
            // Use external handler (como ProductList faz)
            await onFavouriteToggle(!isProductInWishlist)
        } else {
            // Use internal logic (fallback automático)
            const productObj = {productId: productId || id, ...product}
            await toggleWishlist(productObj, tileRef?.current)
        }

        // Update local count
        if (isProductInWishlist) {
            setWishlistCount((prev) => Math.max(0, prev - 1))
            trackRemoveFromWishlistEvent({
                variationGroupId: product.productId,
                value: product.price,
                category: product.primaryCategoryId || product.c_primaryCategory,
                source: 'product-tile'
            })
        } else {
            setWishlistCount((prev) => prev + 1)
            trackAddToWishlistEvent({
                variationGroupId: product.productId,
                value: product.price,
                category: product.primaryCategoryId || product.c_primaryCategory,
                source: 'product-tile'
            })
        }
    }

    // QuickView owns the toggle itself; only mirror the resulting count so the tile stays in sync.
    const handleQuickViewWishlistToggle = (nowFavourite) => {
        setWishlistCount((prev) => (nowFavourite ? prev + 1 : Math.max(0, prev - 1)))
    }

    // Primary image for the tile, the image is determined from the product and selected variation attributes.
    const image = useMemo(
        () =>
            getProductTileImages({
                product,
                imageGroups,
                selectableAttributeId,
                initialVariationValue,
                imageViewType,
                colorValue,
                productId
            }),
        [
            product,
            imageGroups,
            selectableAttributeId,
            initialVariationValue,
            imageViewType,
            colorValue,
            productId
        ]
    )
    // Primary URL user to wrap the ProduceTile.
    // Note: We no longer include color in URL params for SEO purposes.
    // Color is determined by the variation group productId in the URL path.
    const productUrl = useMemo(() => {
        // If QuickView has fetched full product data, use it (has c_relativeUrl)
        let baseUrl
        if (qvProduct) {
            baseUrl = productUrlBuilder(qvProduct)
        } else {
            // If productId was updated by QuickView swatch click (immediate),
            // reconstruct the URL from the current product's URL pattern
            let productForUrl = product
            if (product?.c_relativeUrl && productId) {
                const urlMatch = product.c_relativeUrl.match(/^(.*\/)([^/]+)\.html$/)
                const urlProductId = urlMatch?.[2]
                if (urlProductId && urlProductId !== productId) {
                    productForUrl = {
                        ...product,
                        c_relativeUrl: `${urlMatch[1]}${productId}.html`
                    }
                }
            }
            baseUrl = productUrlBuilder(productForUrl)
        }

        // Append size/length selections from QV as query params so PDP pre-selects them
        const nonColorSelections = Object.fromEntries(
            Object.entries(qvLocalSelections).filter(([key]) => key !== 'color')
        )
        if (baseUrl && Object.keys(nonColorSelections).length > 0) {
            return rebuildPathWithParams(baseUrl, nonColorSelections)
        }
        return baseUrl
    }, [product, qvProduct, productId, qvLocalSelections])

    // NOTE: variationAttributes are only defined for master/variant type products.
    // const variationAttributes = useMemo(() => getDecoratedVariationAttributes(product), [product])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = overwriteProductName || (product.name ?? product.productName)

    const selectedVariationValue = useMemo(() => {
        if (colorValue) return colorValue

        const productIdVariant =
            findVariantByProductId(pricingProduct, productId) ||
            findVariantByProductId(product, productId) ||
            pricingProduct?.variationGroups?.find((group) => group?.productId === productId) ||
            product?.variationGroups?.find((group) => group?.productId === productId)

        return (
            productIdVariant?.variationValues?.[selectableAttributeId] ||
            initialVariationValue ||
            defaultColorVariationValue
        )
    }, [
        colorValue,
        pricingProduct,
        product,
        productId,
        selectableAttributeId,
        initialVariationValue,
        defaultColorVariationValue
    ])

    // Only show the c_mediaProductTile video when the displayed color matches its color.
    const tileVideoColor = productObject?.representedProduct?.c_color
    // Sets do not have a representedProduct, so fall back to the set-level c_mediaProductTile.
    const tileVideoSrc =
        productObject?.representedProduct?.c_mediaProductTile ||
        (isProductSet ? productObject?.c_mediaProductTile : undefined)
    const hasTileVideo =
        showVideo &&
        !!tileVideoSrc &&
        (!tileVideoColor || selectedVariationValue === tileVideoColor)

    const productWithFilteredVariants = useMemo(() => {
        const fallbackVariationValue = initialVariationValue || defaultColorVariationValue
        const getVariantsForValue = (variationValue) =>
            variationValue
                ? pricingProduct?.variants?.filter(
                      ({variationValues}) =>
                          variationValues?.[selectableAttributeId] === variationValue
                  )
                : pricingProduct?.variants
        const selectedVariants = getVariantsForValue(selectedVariationValue)
        const fallbackVariants = getVariantsForValue(fallbackVariationValue)
        const variants = selectedVariants?.length
            ? selectedVariants
            : fallbackVariants?.length
            ? fallbackVariants
            : pricingProduct?.variants
        const effectiveVariationValue = selectedVariants?.length
            ? selectedVariationValue
            : fallbackVariationValue

        return {
            ...pricingProduct,
            variants,
            variationValues: {
                ...pricingProduct?.variationValues,
                ...(effectiveVariationValue
                    ? {[selectableAttributeId]: effectiveVariationValue}
                    : {})
            }
        }
    }, [
        pricingProduct,
        selectableAttributeId,
        selectedVariationValue,
        initialVariationValue,
        defaultColorVariationValue
    ])

    // Pricing is dynamic! Ensure we are showing the right price for the selected variation attribute
    // value. When a variant is selected in QuickView, use that variant's price data.
    const priceData = useMemo(() => {
        // If a variant is selected in QuickView, calculate price for that specific variant
        if (selectedVariantId) {
            const selectedVariant =
                findVariantByProductId(pricingProduct, selectedVariantId) ||
                findVariantByProductId(product, selectedVariantId)

            if (selectedVariant) {
                const selectedSearchVariant = findVariantByProductId(product, selectedVariantId)
                // Use variant's own promotions, or fallback to product-level promotions
                const variantWithPromotions = {
                    ...selectedSearchVariant,
                    ...selectedVariant,
                    productPromotions:
                        selectedVariant.productPromotions ||
                        selectedSearchVariant?.productPromotions ||
                        pricingProduct?.productPromotions ||
                        product?.productPromotions,
                    c_newArrivalDate:
                        selectedVariant?.c_newArrivalDate ||
                        selectedSearchVariant?.c_newArrivalDate ||
                        pricingProduct?.c_newArrivalDate ||
                        product?.c_newArrivalDate
                }
                return getPriceData(variantWithPromotions)
            }
        }
        // No variant selected - show lowest price across all variants
        return getPriceData(productWithFilteredVariants)
    }, [
        productWithFilteredVariants,
        selectedVariantId,
        pricingProduct,
        product
    ])

    // Hold the last resolved price until the new color's price is ready, so it never
    // flashes back to the master range while the color fetch is in flight.
    const lastPriceDataRef = useRef(priceData)
    if (!isSelectedTileProductPriceLoading) {
        lastPriceDataRef.current = priceData
    }
    const displayPriceData = isSelectedTileProductPriceLoading
        ? lastPriceDataRef.current
        : priceData

    // Retrieve product badges
    const filteredLabels = useMemo(() => {
        const labelsMap = new Map()
        if (product?.representedProduct) {
            badgeDetails.forEach((item) => {
                if (
                    item.propertyName &&
                    typeof product.representedProduct[item.propertyName] === 'boolean' &&
                    product.representedProduct[item.propertyName] === true
                ) {
                    // item.label is a variable, so formatjs can't extract it —
                    // ids are registered in ./dynamic-messages.json
                    labelsMap.set(intl.formatMessage(item.label), item.color)
                }
            })
        }
        return labelsMap
    }, [product, badgeDetails])

    const handleNotifyMeModalState = (stateValue) => {
        setIsNotifyMeModalOpen(stateValue)
    }

    const handleShopSimilarModalState = (stateValue) => {
        setIsShopSimilarModalOpen(stateValue)
    }

    const showQuickView = (triggerElement) => {
        setConfirmationTrigger(triggerElement || null)
        onQuickViewOpen?.()
        if (isProductSet && !isDesktop) {
            setSetQuickViewOpen(true)
            return
        }
        if (isDesktop && tileMode !== 'quick-view') {
            setIsQuickViewOpen(true)
            return
        }

        if (isDesktop && tileMode === 'quick-view') {
            setMobileQuickViewOpenInternal(true)
            return
        }

        if (setMobileQuickViewOpen) {
            setQuickViewProduct(productId)
            setQuickViewWishlistInfo({
                wishlistCount: wishlistCount,
                isFavourite: isProductInWishlist
            })
            setMobileQuickViewOpen(true)
        } else {
            setMobileQuickViewOpenInternal(true)
        }
    }

    const variationValues = productWithFilteredVariants?.variationValues || []

    const variant = useVariationByAttributeValues(product, variationValues)

    const {handleCartItem} = useProductTileCart({
        product,
        variant,
        productId,
        id,
        isWishlistPage,
        removeItemFromWishlist,
        ref: tileRef,
        setIsQuickViewOpen,
        isDesktop,
        onAddToCartModalOpen,
        onAddToCartModalClose,
        pathname: location.pathname
    })

    const {handleMouseEnter, handleMouseLeave} = useProductTileHover({
        isOOS,
        isNotifyMeModalOpen,
        isShopSimilarModalOpen,
        setIsHovered,
        setIsQuickViewOpen,
        setMobileQuickViewOpenInternal,
        triggerFreshLikesFetch,
        tileRef
    })
    const {tileMode: hookTileMode, isTouch} = useProductTileMode({
        tileRef,
        productId: productId || id
    })
    /* GDI carousel can override the screensize-based mode picker. When the
     * caller passes `forceTileMode` (string or Chakra responsive object), we
     * resolve to the active breakpoint and prefer it over the hook result.
     * PLP / recommendations / wishlist leave this unset → hook wins as before. */
    const resolvedForceTileMode = useBreakpointValue(
        typeof forceTileMode === 'object' && forceTileMode
            ? forceTileMode
            : forceTileMode
            ? {base: forceTileMode}
            : {}
    )
    const tileMode = resolvedForceTileMode || hookTileMode

    /* Same shape as `forceTileMode`: the GDI block passes a per-breakpoint map
     * because it only owns the swipe where it renders as a draggable carousel.
     * Resolve before use — a raw object is always truthy and would read as
     * "disabled" everywhere. */
    const resolvedDisableImageScroll = Boolean(
        useBreakpointValue(
            typeof disableImageScroll === 'object' && disableImageScroll
                ? disableImageScroll
                : {base: Boolean(disableImageScroll)}
        )
    )

    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'

    // 1-based main-image number (small grid). Prefer the enriched Nth image; before
    // Phase-2 enrichment loads, derive its URL from the first image so it paints
    // immediately (no #1 → #N flash). Falls back to the first image otherwise.
    const firstImageUrl = image[0]?.disBaseLink || image[0]?.link
    const enrichedNthUrl =
        mainImageNumber > 1
            ? image[mainImageNumber - 1]?.disBaseLink || image[mainImageNumber - 1]?.link
            : null
    const mainImageUrl =
        mainImageNumber > 1
            ? enrichedNthUrl ||
              (imageGroups?.length
                  ? firstImageUrl
                  : deriveNthImageUrl(firstImageUrl, mainImageNumber) || firstImageUrl)
            : firstImageUrl
    const mainImage = buildImageUrl(mainImageUrl, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED)

    const hoveredImageUrl = image[1]?.disBaseLink || image[1]?.link
    const hoveredImage = hoveredImageUrl
        ? buildImageUrl(hoveredImageUrl, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED)
        : null

    const mainImages = image
        .map((img) => {
            const url = img?.disBaseLink || img?.link
            return url ? buildImageUrl(url, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED) : null
        })
        .filter(Boolean)

    // Mixed media sequence (images + videos) for carousel — only when allImages data is available
    const mediaItems = useMemo(() => {
        const garageEnabled = getSiteEnvVarValue(appConfig, 'ENABLE_PRODUCT_VIDEO_GARAGE', site?.id) === 'true'
        const dynamiteEnabled = getSiteEnvVarValue(appConfig, 'ENABLE_PRODUCT_VIDEO_DYNAMITE', site?.id) === 'true'

        // Gate by feature flag + brand (use brand context — product.brand is absent on search hits)
        if (isGarage && !garageEnabled) return null
        if (isDynamite && !dynamiteEnabled) return null
        if (!isGarage && !isDynamite) return null

        // imageGroups only available after Phase 2 enrichment loads — bail out until then
        if (!imageGroups?.length) return null

        return buildTileMediaItems({
            imageUrls: mainImages,
            imageGroups,
            // Displayed color; c_color / c_videoSequencePositions live on representedProduct.
            colorValue:
                selectedVariationValue ||
                colorValue ||
                product?.c_color ||
                product?.representedProduct?.c_color,
            videoSequencePositions:
                product?.c_videoSequencePositions ||
                product?.representedProduct?.c_videoSequencePositions ||
                null
        })
    }, [
        mainImages,
        imageGroups,
        selectedVariationValue,
        colorValue,
        product?.c_color,
        product?.representedProduct?.c_color,
        product?.c_videoSequencePositions,
        product?.representedProduct?.c_videoSequencePositions,
        appConfig,
        isGarage,
        isDynamite
    ])
    const showHoverImage =
        hoveredImage &&
        isHydrated() &&
        !isQuickViewOpen &&
        !(isProductTileMediaCarouselEnabled && hoveredImage && isDesktop) &&
        /* Host block owns the swipe: the second slot is unreachable, and this
         * also keeps `mediaImageRow` from widening to 200% for a row of one. */
        !(resolvedDisableImageScroll && !isDesktop)

    const promotionIds = getProductPromotionIds(productWithFilteredVariants)
    const {data} = usePromotions(
        {
            parameters: {
                ids: promotionIds.join(',')
            }
        },
        {
            enabled: promotionIds.length > 0
        }
    )
    const productPromotionObject = data?.data?.find((promo) => promo.c_showCalloutMessage === true)

    const onQuickViewCloseHandler = () => {
        setIsHovered(false)
        setQvLocalSelections({})
    }

    const closeInternalMobileQuickView = () => {
        setMobileQuickViewOpenInternal(false)
        onQuickViewClose?.()
    }

    const styles = useMultiStyleConfig('ProductTile', {
        variant: tileMode === 'quick-view' ? 'QuickView' : stylingVariant,
        tileMode,
        isDesktop,
        isHovered,
        isTouch,
        showHoverImage,
        disableImageScroll: resolvedDisableImageScroll,
        isOOS,
        isQuickViewOpen,
        showScrollHint
    })

    const productTileMediaProps = {
        styles,
        productObject,
        showVideo,
        hasTileVideo,
        tileVideoSrc,
        videoProps,
        responsiveMediaBlock,
        isLayout,
        showHoverImage,
        enableImageCarousel: isProductTileMediaCarouselEnabled,
        disableImageScroll: resolvedDisableImageScroll,
        isDesktop,
        mainImages,
        mediaItems,
        mainImage,
        hoveredImage,
        dynamicImageProps,
        shouldSkipQuickView: ShouldSkipQuickView,
        showQV,
        isGiftCard,
        isHydrated: isHydrated(),
        isQuickViewOpen,
        handleNotifyMeModalState,
        handleShopSimilarModalState,
        setIsQuickViewOpen,
        productId,
        setColorValue,
        setProductId,
        onQuickViewCloseHandler,
        isWishlistPage,
        wishlistProductId,
        onWishlistToggle: handleQuickViewWishlistToggle,
        hasLengthVariant,
        product,
        setSelectedVariantId,
        setQvProduct,
        showQuickView,
        tileMode,
        handleCartItem,
        isHovered,
        responsiveDisplay: isProductSet
            ? {...(responsiveDisplay || {}), quickActionsDisplay: {display: {base: 'flex', lg: 'none'}}}
            : responsiveDisplay,
        onLocalSelectionsChange: setQvLocalSelections
    }
    /* How many rows BELOW title + price the details band has to leave room for.
     * Swatches only render on hover, so their row has to be reserved from the
     * start or the tile grows under the cursor.
     *
     * Badges and swatches do NOT stack: on hover ProductCallout drops a badge
     * row and the swatch row takes its place (it keeps only
     * `validBadges.length - 1` placeholders), so the two compete for the same
     * rows and the tallest state is whichever needs more. A promotion name is
     * the one thing that sits on its own line in both states.
     *
     * Drives which min-height the details wrapper picks. */
    const reservedDetailRows = (() => {
        if (isGiftCard || isProductSet) return 0
        const badges = getVisibleTileBadges(productObject, getTileBadgeRules(appConfig, site?.id))
        /* The desktop swatch row is gated on hover alone, NOT on `showSwatches`
         * (that prop only drives the mobile single swatch), so the only thing
         * that can suppress it is a GDI ColorSwatches toggle. */
        const swatchesEnabled = responsiveDisplay
            ? Boolean(responsiveDisplay.colorSwatchesEnabled)
            : true
        const hasSwatchRow = swatchesEnabled && hasTileColorSwatches(productObject)
        const swappableRows = Math.max(badges.length, hasSwatchRow ? 1 : 0)
        return (productPromotionObject?.calloutMsg || productPromotionObject?.name ? 1 : 0) + swappableRows
    })()

    /* A CMS tile hugs its content only when there is nothing below title +
     * price. With rows to reserve it keeps the theme floor: on hover
     * ProductCallout releases its badge row (it keeps `validBadges.length - 1`
     * placeholders), so an unset min-height would let the band collapse under
     * the cursor. */
    const detailsWrapperOverride =
        responsiveDisplay?.hugDetailsToContent && reservedDetailRows === 0
            ? {minHeight: 'unset'}
            : null

    const productTileDetailsProps = {
        styles,
        localizedProductName,
        isShopTheLook,
        reservedDetailRows,
        detailsWrapperOverride,
        showSwatches: showSwatches && !isProductSet,
        product: productObject,
        isWishlistEnabled,
        showProductLikes: showProductLikes && !isProductSet && !disableFavouriteInteraction,
        isProductInWishlist,
        wishlistCount,
        isHovered,
        handleFavouriteClick,
        isLoadingFreshLikes,
        isWishlistPage,
        productWithFilteredVariants,
        isRefreshingData: isRefreshingData || isSelectedTileProductPriceLoading,
        priceData: displayPriceData,
        currency,
        productPromotionObject,
        isOOS,
        intl,
        colorValue,
        setColorValue,
        setProductId,
        productId,
        isGiftCard,
        responsiveDisplay,
        isProductSet,
        // The swatch row is desktop-only (display:none below lg), so mobile never asks for stock.
        isNearViewport: isNearViewport && Boolean(isDesktop)
    }

    const handleProductLinkClick = useCallback(
        (event) => {
            if (!onClick) {
                return
            }

            const selectedProduct = buildProductTileSelectItemProduct(
                pricingProduct,
                selectedTileProductId,
                {colorValue}
            )

            onClick({
                event,
                product: selectedProduct,
                productSearchItem: selectedProduct,
                productId: selectedTileProductId
            })
        },
        [onClick, pricingProduct, selectedTileProductId, colorValue]
    )

    return (
        <>
            <Box
                {...styles.container}
                {...rest}
                data-product-tile="true"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                ref={setTileRef}
            >
                <Link
                    data-testid="product-tile"
                    to={productUrl}
                    {...styles.link}
                    onClick={handleProductLinkClick}
                >
                    {/* Product tile media */}
                    <ProductTileMedia {...productTileMediaProps} />
                    {showDetails && <ProductTileDetails {...productTileDetailsProps} />}
                </Link>
                {enableFavourite &&
                    isWishlistEnabled &&
                    !disableFavouriteInteraction &&
                    isHydrated() &&
                    !isProductSet && (
                    <Box
                        sx={responsiveDisplay?.wishlistIcon}
                        onClick={(e) => {
                            e.preventDefault()
                        }}
                    >
                        <IconButtonWithRegistration
                            wishlistProductId={wishlistProductId}
                            productId={productId || initialProductId}
                            wishlistClickPage="plp"
                            as={ChakraLink}
                            href={'#'}
                            data-testid="wishlist-button"
                            aria-label={
                                isProductInWishlist
                                    ? intl.formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.remove_from_wishlist',
                                              defaultMessage: 'Remove {product} from wishlist'
                                          },
                                          {product: localizedProductName}
                                      )
                                    : intl.formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.add_to_wishlist',
                                              defaultMessage: 'Add {product} to wishlist'
                                          },
                                          {product: localizedProductName}
                                      )
                            }
                            icon={
                                isProductInWishlist ? (
                                    <HeartSolidIcon {...styles.favIconSolid} />
                                ) : (
                                    <HeartIcon
                                        display={
                                            isHovered || isDesktop === false ? 'inline' : 'none'
                                        }
                                        {...styles.favIconOutline}
                                    />
                                )
                            }
                            {...styles.favIcon}
                            {...styles.favIconButton}
                            onClick={handleFavouriteClick}
                        />
                    </Box>
                )}
            </Box>

            {/* QuickView */}
            {hasOpenedMobileQuickView &&
                (!setMobileQuickViewOpen || (isDesktop && tileMode === 'quick-view')) && (
                <ProductTileMobileQuickView
                    isOpen={mobileQuickViewOpen}
                    onClose={closeInternalMobileQuickView}
                    productId={productId}
                    isProductInWishlist={isProductInWishlist}
                    wishlistCount={wishlistCount}
                    onWishlistToggle={handleQuickViewWishlistToggle}
                    setProductId={setProductId}
                    isWishlistPage={isWishlistPage}
                    wishlistProductId={wishlistProductId}
                    product={product}
                    onVariantSelect={setSelectedVariantId}
                    onProductChange={setQvProduct}
                    onLocalSelectionsChange={setQvLocalSelections}
                    setIsQuickViewOpen={setMobileQuickViewOpenInternal}
                    onQuickViewClose={onQuickViewClose}
                    quickViewVariant={tileMode === 'quick-view' ? 'QuickView' : undefined}
                    forceMobileLayout={isDesktop && tileMode === 'quick-view'}
                    placement={isDesktop && tileMode === 'quick-view' ? 'right' : 'bottom'}
                    motionPreset={
                        isDesktop && tileMode === 'quick-view' ? 'slideInRight' : 'slideInBottom'
                    }
                    size={isDesktop && tileMode === 'quick-view' ? 'md' : 'sm'}
                    height={isDesktop && tileMode === 'quick-view' ? undefined : '85vh'}
                    isFullHeight={isDesktop && tileMode === 'quick-view'}
                />
            )}

            {/* Product Set QuickView */}
            {hasOpenedSetQuickView && (
                <ProductSetQuickView
                    isOpen={setQuickViewOpen}
                    onClose={() => setSetQuickViewOpen(false)}
                    productId={productId || id}
                    productName={localizedProductName}
                />
            )}
        </>
    )
})

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = productTilePropTypes

export default ProductTile
